import React from 'react';
import Button from './Button';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'success' | 'warning' | 'danger' | 'info';
  showCancelButton?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'info',
  showCancelButton = true
}) => {
  if (!isOpen) return null;

  // Define variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          icon: (
            <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          confirmButtonVariant: 'primary' as const
        };
      case 'warning':
        return {
          icon: (
            <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          confirmButtonVariant: 'primary' as const
        };
      case 'danger':
        return {
          icon: (
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          confirmButtonVariant: 'primary' as const
        };
      case 'info':
      default:
        return {
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          confirmButtonVariant: 'primary' as const
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="bg-white rounded-lg shadow-xl z-10 w-full max-w-md mx-4 overflow-hidden transform transition-all">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            {variantStyles.icon}
          </div>
          
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            {title}
          </h3>
          
          <p className="text-center text-gray-600 mb-6">
            {message}
          </p>
          
          <div className="flex justify-center space-x-3">
            {showCancelButton && onCancel && (
              <Button 
                variant="secondary" 
                onClick={onCancel}
                className="w-full"
              >
                {cancelLabel}
              </Button>
            )}
            <Button 
              variant={variantStyles.confirmButtonVariant} 
              onClick={onConfirm}
              className={showCancelButton ? "w-full" : "w-1/2"}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
